/**
 * Row classification utilities for Dr/Cr rendering (§3.1-3.3).
 *
 * Classifies rows from all pairs into Dr and Cr groups based on
 * their entry (Dr/Cr) and adjustment sign, then checks balance.
 */

import {
    isDueToDirectorsCode,
    resolveDueFromToDirectorsSignedAdjustment,
    resolveToPaperFieldsFromPairing,
    rowBelongsToDueFromToDirFromPaper,
    flipDtdDetailRowAdjustment,
    resolveSignedAdjustmentFromBackendLine,
} from '@/pages/components/audit/adjustment/adjust/dialogs/utils/adjustment-helpers';
import {isLoanFieldFsCode, isPpeFieldSyntheticDetail, isIaFieldSyntheticDetail, isRentalFieldWpDetail, LOAN_FIELD_FS_CODES, rentalFieldAttrMapsOutOfSync, rescalePpeFieldAdjKeysForAmount, rescaleIaFieldAdjKeysForAmount, rescaleRentalFieldAttrMapsForAmount, collectNestedPpeFieldAdjScalars, PPE_FIELD_ADJ_KEYS} from '@/pages/components/audit/adjustment/adjust/utils/consolidateWpAdjustments';
import {
    DTD_CL_CODE,
    DTD_DEFAULT_ORIGINAL_ENTRY,
    coerceDtdDrCrEntry,
    convertDtdClUserInput,
    determineDtdClRenderPosition,
    getDtdClEffectiveEntry,
    getPairedDtdClEntry,
    isDtdClCode,
    isFromDtdToPairedInit,
    usesDtdAdjustmentPathScoped,
    getOriginalEntryFromParent,
    resolveDtdUserSignedRowAdjustment,
    resolveDtdToPaperUserEditedRender,
    resolveDtdDynamicRenderPosition,
    shouldUseDtdAuthoritativeLineEntryForRender,
    resolveDtdAuthoritativeLineEntryRender,
    captureDtdClOriginalEntries,
    computeDtdPathFromEffectiveEntry,
    resolveDtdClToOrigEntryForClassify,
    computeFromDtdClEffectiveEntry as computeFromDtdClEffectiveEntryCore,
    resolveToPaperRenderFromDtdPairing as resolveToPaperRenderFromDtdPairingCore,
    type DtdClBsClPairingCtx,
    type DtdClToRenderResult,
} from '@/pages/components/audit/adjustment/adjust/utils/specialAdjustFlow';

export {isDueToDirectorsCode};
export {
    DTD_CL_CODE,
    isDtdClCode,
    determineDtdClRenderPosition,
    getDtdClEffectiveEntry,
    convertDtdClUserInput,
    type DtdClBsClPairingCtx,
    type DtdClToRenderResult,
    isFromDtdToPairedInit,
    resolveDtdClToPairedEntry,
    captureDtdClOriginalEntries,
    resolveDtdClToOrigEntryForClassify,
} from '@/pages/components/audit/adjustment/adjust/utils/specialAdjustFlow';

/** @deprecated 使用 isFromDtdToPairedInit */
export {isFromDtdToPairedInit as isDtdClToPairedInit} from '@/pages/components/audit/adjustment/adjust/utils/specialAdjustFlow';

export function computeFromDtdClEffectiveEntry(
    fromParDetail: any,
    fromAdjustment?: number,
    userEdited?: boolean,
    fromCode?: string,
): 'Dr' | 'Cr' | null {
    return computeFromDtdClEffectiveEntryCore(
        fromParDetail,
        fromAdjustment,
        userEdited,
        fromCode,
        resolveDueFromToDirectorsSignedAdjustment,
        (c, adj) => getDtdClEffectiveEntry(adj, getOriginalEntryFromParent(c)),
    );
}

export function resolveToPaperRenderFromDtdPairing(
    fromPaperCode: string,
    fromAdjustment: number,
    toPaperCode: string,
    specialDtdPathCodes?: ReadonlySet<string>,
): DtdClToRenderResult {
    return resolveToPaperRenderFromDtdPairingCore(
        fromPaperCode,
        fromAdjustment,
        toPaperCode,
        (from, adj, to) => resolveToPaperFieldsFromPairing(from, adj, to, {specialDtdPathCodes}),
    );
}

function resolveDtdOriginalEntry(originalEntry: string): 'Dr' | 'Cr' {
    return coerceDtdDrCrEntry(originalEntry) ?? DTD_DEFAULT_ORIGINAL_ENTRY;
}

/**
 * A row that has been classified into Dr or Cr rendering position.
 */
export interface ClassifiedRow {
    row: any;              // Original row data
    code: string;          // Schedule code (e.g., 'BS-CA-CASH')
    name: string;          // Display name (e.g., 'Bank Interest Income')
    entry: string;         // Original entry ('Dr' | 'Cr')
    adjustment: number;    // Adjustment value
    source: 'from' | 'to'; // Data source
    pairId?: string;       // Which pair this row belongs to
}

export interface ClassifiedRows {
    drRows: ClassifiedRow[];
    crRows: ClassifiedRow[];
}

export interface BalanceResult {
    drTotal: number;
    crTotal: number;
    isBalanced: boolean;
    difference: number;
}

/** Flat equity WP detail: top-level adjustment field (BS-EQ-DIV / BS-EQ-AL) */
export const FLAT_EQUITY_ADJUSTMENT_CODES = new Set(['BS-EQ-DIV', 'BS-EQ-AL']);

/** 工作底稿元数据键（只做字典渲染，不参与调整操作） */
const METADATA_KEYS = new Set(['SME-FRS', 'WOKE_DONE', 'glInvoiceDetails']);

export function isFlatEquityAdjustmentCode(code?: string | null): boolean {
    if (!code) return false;
    return FLAT_EQUITY_ADJUSTMENT_CODES.has(String(code).trim().toUpperCase());
}

/** VO-only adjustments[] from BS-EQ-AL / BS-EQ-DIV WP load (display metadata, not row data). */
const FLAT_EQUITY_VO_ARRAY_KEYS = new Set(['adjustments', 'mpfAdjustments']);

/**
 * detail 已带非空 commonRow（AL CommonWp / DIV toPaper / RND CommonWp）。
 * 无 commonRow 的 DIV/AL flat WP 不受影响。
 */
export function hasAlCommonRowDetail(detail: any): boolean {
    return Array.isArray(detail?.commonRow) && detail.commonRow.length > 0;
}

/**
 * AdjustTabsDialog：`_synthetic` 回写 commonRow、提交按渲染列同步 entry。
 * 仅这些 code + 实际有 commonRow 时生效（见 isCommonRowSyntheticEditDetail）。
 */
export const COMMON_ROW_SYNTHETIC_EDIT_CODES = new Set(['BS-EQ-AL', 'BS-EQ-DIV', 'BS-CL-RND']);

export function isCommonRowSyntheticEditCode(code?: string | null): boolean {
    if (!code) return false;
    return COMMON_ROW_SYNTHETIC_EDIT_CODES.has(String(code).trim().toUpperCase());
}

/** 仅当 code 在白名单且 detail 已有 commonRow 时走 synthetic→commonRow；flat DIV/AL 不进入。 */
export function isCommonRowSyntheticEditDetail(code?: string | null, detail?: any): boolean {
    return isCommonRowSyntheticEditCode(code) && hasAlCommonRowDetail(detail);
}

/** flat equity：无 commonRow 时保持原 flat；仅有 commonRow 的 AL/DIV 才退出 flat。 */
export function isFlatEquityDetailForCode(code?: string | null, detail?: any): boolean {
    if (!isFlatEquityAdjustmentCode(code)) return false;
    if (hasAlCommonRowDetail(detail)) return false;
    return true;
}

function sumFlatEquityVoAdjustments(detail: any): number {
    if (!detail || !Array.isArray(detail.adjustments) || detail.adjustments.length === 0) {
        return 0;
    }
    return detail.adjustments.reduce(
        (sum: number, a: any) => sum + (Number(a?.adjustment) || 0),
        0,
    );
}

/**
 * Resolve signed adjustment for flat equity detail.
 * Prefers top-level adjustment; falls back to sum(adjustments[]) then explicit fallback.
 */
export function resolveFlatEquityAdjustment(detail: any, fallback = 0): number {
    if (!detail || typeof detail !== 'object') return fallback;
    if ('adjustment' in detail && detail.adjustment !== undefined && detail.adjustment !== null) {
        const v = Number(detail.adjustment);
        if (!Number.isNaN(v) && Math.abs(v) >= 0.001) return v;
    }
    const voSum = sumFlatEquityVoAdjustments(detail);
    if (Math.abs(voSum) >= 0.001) return voSum;
    return fallback;
}

/**
 * BS-EQ-AL / BS-EQ-DIV：effectiveEntry 已由 normalize / merge 解析时直接采用；
 * 勿将 effectiveEntry 当作 FsCategory originalEntry 再跑 determineRenderPosition（会翻转 Dr↔Cr）。
 */
export function resolveFlatEquityRenderEntry(
    effectiveEntry: string | undefined,
    fsOriginalEntry: string,
    rawAdjustment: number,
): 'Dr' | 'Cr' {
    if (effectiveEntry === 'Dr' || effectiveEntry === 'Cr') {
        return effectiveEntry;
    }
    return determineRenderPosition(fsOriginalEntry || 'Cr', rawAdjustment);
}

function hasFlatEquityRowArrays(parDetail: any): boolean {
    if (!parDetail || typeof parDetail !== 'object') return false;
    return Object.entries(parDetail).some(
        ([key, value]) =>
            !METADATA_KEYS.has(key)
            && !FLAT_EQUITY_VO_ARRAY_KEYS.has(key)
            && Array.isArray(value),
    );
}

function scanFlatEquityAdjustmentRows(
    parDetail: any,
    includeZero = false,
): AdjustedRowInfo[] {
    if (!parDetail || typeof parDetail !== 'object') {
        return [];
    }
    if (hasFlatEquityRowArrays(parDetail)) return [];

    const adj = resolveFlatEquityAdjustment(parDetail);
    if (!includeZero && Math.abs(adj) < 0.001) return [];

    const row = {
        item: '',
        perClient: Number(parDetail.perClient) || 0,
        adjustment: adj,
    };
    return [buildRowInfo('_synthetic', 0, row, adj)];
}

/**
 * Determine the rendering position (Dr or Cr) for a row
 * based on its entry and adjustment sign (§3.1).
 *
 * | Entry | adjustment | Position |
 * |-------|-----------|----------|
 * | Dr    | > 0       | Dr       |
 * | Dr    | < 0       | Cr       |
 * | Cr    | > 0       | Cr       |
 * | Cr    | < 0       | Dr       |
 */
export function determineRenderPosition(entry: string, adjustment: number): 'Dr' | 'Cr' {
    const isDr = entry?.toUpperCase?.().startsWith('D');
    if (isDr) {
        return adjustment >= 0 ? 'Dr' : 'Cr';
    }
    // Cr entry
    return adjustment >= 0 ? 'Cr' : 'Dr';
}

/** Map FS node entry to render column */
export function entryToRenderPosition(entry: string): 'Dr' | 'Cr' {
    return entry?.toUpperCase?.().startsWith('D') ? 'Dr' : 'Cr';
}

export function usesStaticRenderPosition(code: string, staticPositionCodes?: Set<string>): boolean {
    return staticPositionCodes?.has(String(code || '').trim().toUpperCase()) ?? false;
}

/** Fixed by line.entry when static; dynamic Dr/Cr switch after user edit */
export function resolveRenderPosition(
    code: string,
    entry: string,
    adjustment: number,
    staticPositionCodes?: Set<string>,
): 'Dr' | 'Cr' {
    if (usesStaticRenderPosition(code, staticPositionCodes)) {
        return entryToRenderPosition(entry);
    }
    return determineRenderPosition(entry, adjustment);
}

/**
 * toPaper 端点渲染列：静态模式使用有效 entry（后端 line.entry），
 * 动态模式使用 FS 原始 originalEntry 与 adjustment 推算。
 */
export function resolveToEndpointRenderPosition(
    code: string,
    originalEntry: string,
    adjustment: number,
    staticPositionCodes?: Set<string>,
    effectiveEntry?: string | null,
): 'Dr' | 'Cr' {
    if (usesStaticRenderPosition(code, staticPositionCodes)) {
        return entryToRenderPosition(effectiveEntry ?? originalEntry);
    }
    return determineRenderPosition(originalEntry, adjustment);
}

/**
 * 根据 detail 行汇总 adjustment，计算与 AdjustDrCrView 一致的有效 entry。
 */
export function computeEffectiveEntry(detail: any, originalEntry: string): string {
    if (!detail) return originalEntry;
    const totalAdj = sumDetailRawAdjustments(detail);
    if (Math.abs(totalAdj) < 0.001) return originalEntry;
    return determineRenderPosition(originalEntry, totalAdj);
}

function isDrEntry(entry: string): boolean {
    return entry?.toUpperCase?.().startsWith('D') ?? false;
}

/**
 * 与 AdjustDrCrView.getSignMultiplier 一致：
 * 渲染列与节点 entry 同侧 → 正数；异侧 → 负数。
 */
export function getAdjustmentSignMultiplier(originalEntry: string, renderPosition: 'Dr' | 'Cr'): number {
    return isDrEntry(originalEntry) === (renderPosition === 'Dr') ? 1 : -1;
}

/**
 * 将 AdjustDrCrView 用户输入（可含前导负号）转为 detail 中存储的 adjustment。
 * 前导负号表示切换到与「同侧为正」相反的 Dr/Cr 列；绝对值不变时结果为原 adjustment 的反数。
 */
export function computeStoredAdjustmentFromUserInput(
    originalEntry: string,
    renderPosition: 'Dr' | 'Cr',
    userVal: number,
    previousAdjustment?: number,
): number {
    const absVal = Math.abs(userVal);
    if (absVal < 0.001) return 0;

    const sameSidePositive = getAdjustmentSignMultiplier(originalEntry, renderPosition) > 0;
    const userInputNegative = userVal < 0;
    const storedPositive = userInputNegative ? !sameSidePositive : sameSidePositive;
    const stored = storedPositive ? absVal : -absVal;

    if (previousAdjustment !== undefined && Math.abs(previousAdjustment) >= 0.001) {
        const prevAbs = Math.abs(previousAdjustment);
        if (Math.abs(absVal - prevAbs) < 0.001 && Math.sign(stored) !== Math.sign(previousAdjustment)) {
            return -previousAdjustment;
        }
    }

    return stored;
}

/**
 * 按节点在 AdjustDrCrView 中的渲染列规范化 adjustment 符号：
 * 渲染列与节点 entry 同侧 → 正数；异侧 → 负数。
 */
function normalizeAdjustmentForRenderColumn(
    originalEntry: string,
    rawAdjustment: number,
    renderPosition: 'Dr' | 'Cr',
): number {
    if (Math.abs(rawAdjustment) < 0.001) return rawAdjustment;
    const sign = getAdjustmentSignMultiplier(originalEntry, renderPosition);
    return sign * Math.abs(rawAdjustment);
}

function sumDetailRawAdjustments(detail: any): number {
    if (!detail || typeof detail !== 'object') return 0;

    if (!hasFlatEquityRowArrays(detail)) {
        const flatAdj = resolveFlatEquityAdjustment(detail);
        if (Math.abs(flatAdj) >= 0.001) return flatAdj;
    }

    return scanAllRows(detail).reduce((sum, row) => sum + row.adjustment, 0);
}

function normalizeRowAdjustments(
    originalEntry: string,
    row: any,
    renderPosition: 'Dr' | 'Cr',
): { row: any } {
    if (!row || typeof row !== 'object') return {row};

    const updatedRow = {...row};

    if ('adjustment' in row) {
        const rawAdj = Number(row.adjustment) || 0;
        updatedRow.adjustment = normalizeAdjustmentForRenderColumn(originalEntry, rawAdj, renderPosition);
    }

    if (row.mpf && typeof row.mpf === 'object' && 'adjustment' in row.mpf) {
        const mpfRaw = Number(row.mpf.adjustment) || 0;
        updatedRow.mpf = {
            ...row.mpf,
            adjustment: normalizeAdjustmentForRenderColumn(originalEntry, mpfRaw, renderPosition),
        };
    }

    return {row: updatedRow};
}

/**
 * 提交前规范化 detail：line.entry 取 AdjustDrCrView 渲染列，details.adjustment 符号与节点 entry 及渲染列一致。
 *
 * @param renderPosition AdjustDrCrView 中该节点当前渲染列；未传时根据 detail 汇总值推算
 */
export function normalizeDetailForSubmit(
    detail: any,
    originalEntry: string,
    renderPosition?: 'Dr' | 'Cr',
    /** When set, loan / PL-FC-LI branches only run for loan-field FS codes */
    code?: string,
): { detail: any; effectiveEntry: 'Dr' | 'Cr' } {
    if (!detail || typeof detail !== 'object') {
        const effectiveEntry = renderPosition ?? determineRenderPosition(originalEntry, 0);
        return {detail, effectiveEntry};
    }

    const codeUpper = String(code || '').trim().toUpperCase();
    // Loan / PL-FC-LI branches only when caller passes a loan-field FS code (no structural-only match)
    const allowLoanFieldBranch = LOAN_FIELD_FS_CODES.has(codeUpper);

    // Bank/Vehicle loan field WP→WP only (scoped by code when provided)
    const loanKey = Array.isArray(detail.bankLoans)
        ? 'bankLoans'
        : Array.isArray(detail.vehicleLoans)
            ? 'vehicleLoans'
            : null;
    if (loanKey && allowLoanFieldBranch) {
        const rawAdj = Number(detail.adjustment) || 0;
        const effectiveEntry = renderPosition ?? determineRenderPosition(originalEntry, rawAdj);
        const loans = ((detail[loanKey] as any[]) || []).map((loan: any) => {
            if (!loan || typeof loan !== 'object') return loan;
            const {adjustments: _adj, ...rest} = loan;
            return rest;
        });
        return {
            detail: {
                ...detail,
                [loanKey]: loans,
                adjustment: normalizeAdjustmentForRenderColumn(originalEntry, rawAdj, effectiveEntry),
            },
            effectiveEntry,
        };
    }

    // PL-FC-LI Interest Summary GET shape only（展示态）：仅同步 adjustment 符号，不改数据结构
    if (
        codeUpper === 'PL-FC-LI'
        && detail.interestSummary != null
        && typeof detail.interestSummary === 'object'
        && !Array.isArray(detail.interestSummary)
    ) {
        const rawAdj = Number(detail.adjustment) || 0;
        const effectiveEntry = renderPosition ?? determineRenderPosition(originalEntry, rawAdj);
        return {
            detail: {
                ...detail,
                adjustment: normalizeAdjustmentForRenderColumn(originalEntry, rawAdj, effectiveEntry),
            },
            effectiveEntry,
        };
    }

    // PL-AE-REN field WP→WP only：同步 adjustment 符号；仅在 map/aggregate 与金额不同步时 rescale
    if (
        codeUpper === 'PL-AE-REN'
        && isRentalFieldWpDetail(detail)
    ) {
        const rawAdj = Number(detail.adjustment) || 0;
        const effectiveEntry = renderPosition ?? determineRenderPosition(originalEntry, rawAdj);
        const signedAdj = normalizeAdjustmentForRenderColumn(originalEntry, rawAdj, effectiveEntry);
        if (rentalFieldAttrMapsOutOfSync(detail, signedAdj)) {
            return {
                detail: rescaleRentalFieldAttrMapsForAmount(detail, signedAdj),
                effectiveEntry,
            };
        }
        return {
            detail: {
                ...detail,
                adjustment: signedAdj,
            },
            effectiveEntry,
        };
    }

    // PPE field WP→WP only：同步 adjustment 符号，并 rescale costAdj/addAdj/… markers
    if (isPpeFieldSyntheticDetail(detail, codeUpper)) {
        const nested = collectNestedPpeFieldAdjScalars(detail);
        let nestedSum = 0;
        for (const k of PPE_FIELD_ADJ_KEYS) {
            const fromRow = Number(nested.fieldSums[k]) || 0;
            const fromTop = Number(detail[k]) || 0;
            nestedSum += Math.abs(fromRow) >= 0.001 ? fromRow : fromTop;
        }
        // Prefer top-level adjustment; fall back to nested/top field markers (fromPaper nest shape)
        const rawAdj = Number(detail.adjustment) || nestedSum || 0;
        const effectiveEntry = renderPosition ?? determineRenderPosition(originalEntry, rawAdj);
        const signedAdj = normalizeAdjustmentForRenderColumn(originalEntry, rawAdj, effectiveEntry);
        return {
            detail: rescalePpeFieldAdjKeysForAmount(detail, signedAdj),
            effectiveEntry,
        };
    }

    // IA field WP→WP：同步 adjustment 符号，并 rescale amorChgAdj/nbvCfAdj（slim）
    if (isIaFieldSyntheticDetail(detail, codeUpper)) {
        const rawAdj = Number(detail.adjustment)
            || Number(detail.nbvCfAdj)
            || Number(detail.amorChgAdj)
            || 0;
        const effectiveEntry = renderPosition ?? determineRenderPosition(originalEntry, rawAdj);
        const signedAdj = normalizeAdjustmentForRenderColumn(originalEntry, rawAdj, effectiveEntry);
        return {
            detail: rescaleIaFieldAdjKeysForAmount(detail, signedAdj, codeUpper),
            effectiveEntry,
        };
    }

    const totalRawAdj = sumDetailRawAdjustments(detail);
    const effectiveEntry = renderPosition ?? determineRenderPosition(originalEntry, totalRawAdj);

    if (!hasFlatEquityRowArrays(detail)) {
        const rawAdj = resolveFlatEquityAdjustment(detail);
        if (Math.abs(rawAdj) >= 0.001 || 'adjustment' in detail) {
            return {
                detail: {
                    perClient: Number(detail.perClient) || 0,
                    adjustment: normalizeAdjustmentForRenderColumn(originalEntry, rawAdj, effectiveEntry),
                },
                effectiveEntry,
            };
        }
    }

    const normalized: Record<string, any> = {...detail};

    for (const [key, value] of Object.entries(detail)) {
        if (METADATA_KEYS.has(key)) continue;
        if (!Array.isArray(value)) continue;

        normalized[key] = (value as any[]).map((row: any) => {
            const {row: updatedRow} = normalizeRowAdjustments(originalEntry, row, effectiveEntry);
            return updatedRow;
        });
    }

    return {
        detail: normalized,
        effectiveEntry,
    };
}

/**
 * WP_TO_FS 目标行无 details 时，根据 from 侧有效 entry 推导 to 侧 entry（与 AdjustTabsDialog 配对逻辑一致）。
 */
export function computePairedToEntry(fromEffectiveEntry: string, toOriginalEntry: string): string {
    const normalizedTo = toOriginalEntry || 'Dr';
    if (fromEffectiveEntry === normalizedTo) {
        return fromEffectiveEntry === 'Dr' ? 'Cr' : 'Dr';
    }
    return normalizedTo;
}

/**
 * Classify rows into Dr and Cr groups (§3.2).
 * Rows with adjustment ≈ 0 are excluded.
 */
export function classifyAllRows(
    fromRows: Array<{ row: any; code: string; name: string; entry: string; adjustment: number; pairId?: string }>,
    toRows: Array<{ row: any; code: string; name: string; entry: string; adjustment: number; pairId?: string }>,
): ClassifiedRows {
    const drRows: ClassifiedRow[] = [];
    const crRows: ClassifiedRow[] = [];

    const classify = (
        items: Array<{ row: any; code: string; name: string; entry: string; adjustment: number; pairId?: string }>,
        source: 'from' | 'to',
    ) => {
        for (const item of items) {
            if (Math.abs(item.adjustment) < 0.01) continue; // Skip zero adjustments

            const position = determineRenderPosition(item.entry, item.adjustment);
            const classified: ClassifiedRow = {
                ...item,
                source,
            };

            if (position === 'Dr') {
                drRows.push(classified);
            } else {
                crRows.push(classified);
            }
        }
    };

    classify(fromRows, 'from');
    classify(toRows, 'to');

    return {drRows, crRows};
}

/**
 * Check Dr/Cr balance (§3.3).
 * Uses raw adjustment values (not absolute values).
 * Tolerance: 0.01
 */
export function checkBalance(classified: ClassifiedRows): BalanceResult {
    const drTotal = classified.drRows.reduce((sum, item) => sum + item.adjustment, 0);
    const crTotal = classified.crRows.reduce((sum, item) => sum + item.adjustment, 0);
    const difference = drTotal - crTotal;
    const isBalanced = Math.abs(difference) <= 0.01;

    return {drTotal, crTotal, isBalanced, difference};
}

/**
 * Format a number as a display amount with 2 decimal places.
 */
export function formatAmount(value: number): string {
    return Math.abs(value).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
    });
}

// ─── Row-level extraction from parDetail ────────────────────────────────

/**
 * Information about a single row extracted from a working paper's parDetail.
 */
export interface AdjustedRowInfo {
    arrayName: string;      // Property name in parDetail (e.g., 'commonRow', 'bankRow')
    rowIndex: number;       // Index in the original array
    row: any;               // Full row data
    adjustment: number;     // Adjustment value
    rowKey: string;         // Display label for the row (item / directorName / name)
    perClient: number;      // Per client amount
    subField?: string;      // Nested sub-field path for adjustment (e.g., 'mpf' for row.mpf.adjustment)
}

/**
 * RowSelector label for bankRow: `Bank Name (Account No)`.
 * Falls back to `Row n` only when both fields are blank.
 */
export function formatBankCashRowSelectionLabel(row: any, idx: number): string {
    const name = String(row?.bankName ?? '').trim();
    const acct = String(row?.accountNumber ?? '').trim();
    if (name && acct) return `${name} (${acct})`;
    if (name) return name;
    if (acct) return `(${acct})`;
    return `Row ${idx + 1}`;
}

function buildRowInfo(key: string, idx: number, row: any, adj: number, subField?: string): AdjustedRowInfo {
    const basePerClient = Number(row.perClient || row.salary) || 0;
    const mpfAmount = (subField === 'mpf') ? (Number(row.mpf?.mpf) || 0) : 0;
    const perClient = subField === 'mpf' ? mpfAmount : basePerClient;
    const rowKey = subField === 'mpf'
        ? `${row.name || row.item || `Row ${idx + 1}`} (MPF)`
        : key === 'bankRow'
            ? formatBankCashRowSelectionLabel(row, idx)
            : (row.item || row.directorName || row.name || `Row ${idx + 1}`);

    return {
        arrayName: key,
        rowIndex: idx,
        row,
        adjustment: adj,
        rowKey,
        perClient,
        subField,
    };
}

function extractMpfRows(key: string, idx: number, row: any): AdjustedRowInfo[] {
    const result: AdjustedRowInfo[] = [];
    if (row.mpf && typeof row.mpf === 'object' && 'adjustment' in row.mpf) {
        const mpfAdj = Number(row.mpf.adjustment) || 0;
        if (Math.abs(mpfAdj) >= 0.001) {
            result.push(buildRowInfo(key, idx, row, mpfAdj, 'mpf'));
        }
    }
    return result;
}

/**
 * Scan a parDetail object for all rows that have non-zero adjustments.
 * Works generically across all component types by scanning all array properties
 * whose elements carry an `adjustment` field.
 */
export function scanForAdjustedRows(parDetail: any): AdjustedRowInfo[] {
    if (!parDetail || typeof parDetail !== 'object') return [];

    const result: AdjustedRowInfo[] = [];

    for (const [key, value] of Object.entries(parDetail)) {
        if (METADATA_KEYS.has(key)) continue;
        if (FLAT_EQUITY_VO_ARRAY_KEYS.has(key)) continue;
        if (!Array.isArray(value)) continue;

        (value as any[]).forEach((row: any, idx: number) => {
            if (!row || typeof row !== 'object') return;
            if (!('adjustment' in row)) return;

            const adj = Number(row.adjustment) || 0;
            if (Math.abs(adj) >= 0.001) {
                result.push(buildRowInfo(key, idx, row, adj));
            }

            result.push(...extractMpfRows(key, idx, row));
        });
    }

    result.push(...scanFlatEquityAdjustmentRows(parDetail, false));

    return result;
}

/**
 * Scan ALL rows in a parDetail (including those with adjustment = 0).
 * Used for the "Add Row" feature to show selectable rows.
 */
export function scanAllRows(parDetail: any): AdjustedRowInfo[] {
    if (!parDetail || typeof parDetail !== 'object') return [];

    const result: AdjustedRowInfo[] = [];

    for (const [key, value] of Object.entries(parDetail)) {
        if (METADATA_KEYS.has(key)) continue;
        if (FLAT_EQUITY_VO_ARRAY_KEYS.has(key)) continue;
        if (!Array.isArray(value)) continue;

        (value as any[]).forEach((row: any, idx: number) => {
            if (!row || typeof row !== 'object') return;
            // directorName: DueFromToDir 规范化后仅有 directorName（无 item/perClient）的董事行，含 Non existing
            if (
                !('adjustment' in row)
                && !('perClient' in row)
                && !('item' in row)
                && !('salary' in row)
                && !('name' in row)
                && !('directorName' in row)
            ) return;

            const adj = Number(row.adjustment) || 0;
            result.push(buildRowInfo(key, idx, row, adj));

            if (row.mpf && typeof row.mpf === 'object' && 'adjustment' in row.mpf) {
                const mpfAdj = Number(row.mpf.adjustment) || 0;
                result.push(buildRowInfo(key, idx, row, mpfAdj, 'mpf'));
            }
        });
    }

    result.push(...scanFlatEquityAdjustmentRows(parDetail, true));

    return result;
}

/**
 * A row classified and ready for Dr/Cr grouped rendering.
 */
export interface DrCrRenderItem extends AdjustedRowInfo {
    code: string;           // Schedule code
    codeName: string;       // Display name for the schedule
    entry: string;          // Effective entry (= render column)
    originalEntry?: string; // FS node original entry (for adjustment sign)
    source: 'from' | 'to';
    toIndex?: number;       // Index in toPaper.codes for 'to' source
    position: 'Dr' | 'Cr'; // Rendering position
    isSynthetic?: boolean;  // 当 details 为空时生成的合成行，以 FS_TO_FS 只读样式渲染
}

/**
 * BS-CA-DIR / BS-CL-DTD：按 from/to 规则重新分列并同步 effective entry
 */
export function applyBsClDtdRenderRules(
    drItems: DrCrRenderItem[],
    crItems: DrCrRenderItem[],
    ctx: DtdClBsClPairingCtx,
    origEntries: Record<string, string> = {},
    staticPositionCodes?: Set<string>,
): { drItems: DrCrRenderItem[]; crItems: DrCrRenderItem[] } {
    const {fromCode, fromAdjustment, fromDtdClUserEdited} = ctx;
    const newDr: DrCrRenderItem[] = [];
    const newCr: DrCrRenderItem[] = [];

    const placeItem = (item: DrCrRenderItem) => {
        // 编辑已有调整：后端 line.entry 已确定渲染列，跳过 DTD 配对重算
        if (usesStaticRenderPosition(item.code, staticPositionCodes)) {
            (item.position === 'Dr' ? newDr : newCr).push(item);
            return;
        }

        if (item.source === 'from' && isDtdClCode(fromCode) && item.code === fromCode) {
            if (fromDtdClUserEdited) {
                const rowForSide = {
                    perClient: Number(item.row?.perClient ?? item.perClient) || 0,
                    code: item.row?.code ?? item.code,
                    adjustment: Number(item.adjustment) || 0,
                };
                if (!rowBelongsToDueFromToDirFromPaper(fromCode, rowForSide)) {
                    (item.position === 'Dr' ? newDr : newCr).push(item);
                    return;
                }
                const userSigned = fromAdjustment !== undefined && Math.abs(fromAdjustment) >= 0.001
                    ? Number(fromAdjustment)
                    : -Number(item.adjustment) || 0;
                const originalEntry = getOriginalEntryFromParent(fromCode);
                const position = determineDtdClRenderPosition(userSigned, originalEntry);
                const updated: DrCrRenderItem = {
                    ...item,
                    position,
                    entry: position,
                    adjustment: userSigned,
                };
                (position === 'Dr' ? newDr : newCr).push(updated);
                return;
            }
            const rowForSide = {
                perClient: Number(item.row?.perClient ?? item.perClient) || 0,
                code: item.row?.code ?? item.code,
                adjustment: Number(item.adjustment) || 0,
            };
            if (!rowBelongsToDueFromToDirFromPaper(fromCode, rowForSide)) {
                (item.position === 'Dr' ? newDr : newCr).push(item);
                return;
            }
            const rowAdj = Number(item.adjustment) || 0;
            const adjForPosition = resolveDtdUserSignedRowAdjustment(rowAdj, fromAdjustment, fromDtdClUserEdited);
            const dtdOrig = getOriginalEntryFromParent(fromCode);
            const position = determineDtdClRenderPosition(adjForPosition, dtdOrig);
            const effectiveEntry = getDtdClEffectiveEntry(adjForPosition, dtdOrig);
            const updated: DrCrRenderItem = {
                ...item,
                position,
                originalEntry: item.originalEntry ?? origEntries[item.code] ?? item.entry,
                entry: effectiveEntry,
                adjustment: adjForPosition,
            };
            (position === 'Dr' ? newDr : newCr).push(updated);
            return;
        }

        if (item.source === 'to' && isDtdClCode(fromCode)
            && isDtdClCode(item.code)
            && isFromDtdToPairedInit(ctx, fromCode, item.code)) {
            const lineFromAdj = fromAdjustment ?? item.adjustment;
            const {entry, position, storedAdj} = resolveToPaperRenderFromDtdPairing(
                fromCode,
                lineFromAdj,
                item.code,
            );
            const updated: DrCrRenderItem = {...item, position, entry: position, adjustment: storedAdj};
            (position === 'Dr' ? newDr : newCr).push(updated);
            return;
        }

        (item.position === 'Dr' ? newDr : newCr).push(item);
    };

    drItems.forEach(placeItem);
    crItems.forEach(placeItem);
    return {drItems: newDr, crItems: newCr};
}

/** 按 position 重新分列，确保 Dr/Cr 数组与渲染列一致 */
export function partitionRenderItemsByPosition(
    drItems: DrCrRenderItem[],
    crItems: DrCrRenderItem[],
): { drItems: DrCrRenderItem[]; crItems: DrCrRenderItem[] } {
    const nextDr: DrCrRenderItem[] = [];
    const nextCr: DrCrRenderItem[] = [];
    for (const item of [...drItems, ...crItems]) {
        (item.position === 'Dr' ? nextDr : nextCr).push(item);
    }
    return {drItems: nextDr, crItems: nextCr};
}

/** 根据 code 与 adjustment 符号统一推算 Dr/Cr 渲染列（含 DTD 专用规则）。 */
export function resolveDynamicRenderPosition(
    code: string,
    adjustment: number,
    originalEntry: string,
    specialDtdPathCodes?: ReadonlySet<string>,
): 'Dr' | 'Cr' {
    if (usesDtdAdjustmentPathScoped(code, specialDtdPathCodes)) {
        const orig = getOriginalEntryFromParent(code);
        return resolveDtdDynamicRenderPosition(adjustment, orig);
    }
    return determineRenderPosition(originalEntry, adjustment);
}

/**
 * Classify adjusted rows from all endpoints into Dr and Cr groups.
 * Optionally includes extra rows that the user manually added (even if adjustment = 0).
 */
export function classifyEndpointRows(
    fromCode: string,
    fromName: string,
    fromOriginalEntry: string,
    fromDetail: any,
    toCodes: string[],
    toNames: string[],
    toEntries: (string | null)[],
    toDetails: any[],
    extraRowKeys?: Set<string>,
    staticPositionCodes?: Set<string>,
    toEffectiveEntries?: (string | null)[],
    fromEffectiveEntry?: string | null,
): { drItems: DrCrRenderItem[]; crItems: DrCrRenderItem[] } {
    const drItems: DrCrRenderItem[] = [];
    const crItems: DrCrRenderItem[] = [];

    // Helper: include a row if adj ≠ 0 OR it's in the manually-added set
    const shouldInclude = (code: string, arrayName: string, rowIndex: number, adj: number): boolean => {
        if (Math.abs(adj) >= 0.001) return true;
        if (extraRowKeys?.has(`${code}:${arrayName}:${rowIndex}`)) return true;
        return false;
    };

    // Scan with optional extra-row inclusion
    const scanWithExtras = (parDetail: any, code: string): AdjustedRowInfo[] => {
        if (!parDetail || typeof parDetail !== 'object') return [];
        const result: AdjustedRowInfo[] = [];
        for (const [key, value] of Object.entries(parDetail)) {
            if (!Array.isArray(value)) continue;
            (value as any[]).forEach((row: any, idx: number) => {
                if (!row || typeof row !== 'object') return;
                if (
                    !('adjustment' in row)
                    && !('perClient' in row)
                    && !('item' in row)
                    && !('salary' in row)
                    && !('name' in row)
                    && !('directorName' in row)
                ) return;

                const adj = Number(row.adjustment) || 0;
                if (shouldInclude(code, key, idx, adj)) {
                    result.push(buildRowInfo(key, idx, row, adj));
                }

                if (row.mpf && typeof row.mpf === 'object' && 'adjustment' in row.mpf) {
                    const mpfAdj = Number(row.mpf.adjustment) || 0;
                    const mpfKey = `${code}:${key}:${idx}:mpf`;
                    if (Math.abs(mpfAdj) >= 0.001 || extraRowKeys?.has(mpfKey)) {
                        result.push(buildRowInfo(key, idx, row, mpfAdj, 'mpf'));
                    }
                }
            });
        }
        if (shouldInclude(code, '_synthetic', 0, Number(parDetail.adjustment) || 0)) {
            result.push(...scanFlatEquityAdjustmentRows(parDetail, true));
        }
        return result;
    };

    // From endpoint: 动态分列使用 FS 原始 entry；静态模式使用有效 entry（后端 line.entry）
    const effectiveFromEntry = fromEffectiveEntry ?? fromOriginalEntry;
    const fromRows = scanWithExtras(fromDetail, fromCode);
    for (const row of fromRows) {
        const position = resolveToEndpointRenderPosition(
            fromCode,
            fromOriginalEntry,
            row.adjustment,
            staticPositionCodes,
            effectiveFromEntry,
        );
        const item: DrCrRenderItem = {
            ...row,
            code: fromCode,
            codeName: fromName,
            originalEntry: fromOriginalEntry,
            entry: position,
            source: 'from',
            position,
        };
        (position === 'Dr' ? drItems : crItems).push(item);
    }

    // To endpoints: originalEntry 来自 FS 节点；静态渲染列使用有效 entry
    toCodes.forEach((toCode, toIndex) => {
        const originalEntry = toEntries[toIndex] || 'Cr';
        const effectiveEntry = toEffectiveEntries?.[toIndex] ?? originalEntry;
        const rows = scanWithExtras(toDetails[toIndex], toCode);
        for (const row of rows) {
            const position = resolveToEndpointRenderPosition(
                toCode,
                originalEntry,
                row.adjustment,
                staticPositionCodes,
                effectiveEntry,
            );
            const item: DrCrRenderItem = {
                ...row,
                code: toCode,
                codeName: toNames[toIndex] || toCode,
                originalEntry,
                entry: position,
                source: 'to',
                toIndex,
                position,
            };
            (position === 'Dr' ? drItems : crItems).push(item);
        }
    });

    return {drItems, crItems};
}

/** lines 渲染上下文：每条 adjustment line 统一描述 */
export interface LineDrCrContext {
    code: string;
    codeName: string;
    originalEntry: string;
    /**
     * toPaper 有效 entry：初始化来自配对，用户编辑后来自 resolveEntryFromFsCategory（均写入 toPaper.entries）。
     */
    effectiveEntry?: string | null;
    parDetail: any;
    lineAdjustment: number;
    source: 'from' | 'to';
    toIndex?: number;
}

/** 从 detail 汇总或 line.adjustmentAmount 解析带符号 line 级 adjustment */
export function resolveLineSignedAdjustment(
    parDetail: any,
    lineAdjustmentAmount: number,
    code: string,
    backendRender?: {
        effectiveEntry?: string | null;
        useBackendLineAmount?: boolean;
        /** WP 发起 adjustment 时 line.entry 已确定，不以 detail 行汇总覆盖 */
        useAuthoritativeLineEntry?: boolean;
        specialDtdPathCodes?: ReadonlySet<string>;
    },
): number {
    const lineAmount = Number(lineAdjustmentAmount) || 0;
    const effectiveEntry = backendRender?.effectiveEntry === 'Dr' || backendRender?.effectiveEntry === 'Cr'
        ? backendRender.effectiveEntry
        : null;

    // 非 DTD：API 回显 / WP 发起时 line.entry + adjustmentAmount 为准
    if (
        effectiveEntry
        && !usesDtdAdjustmentPathScoped(code, backendRender?.specialDtdPathCodes)
        && Math.abs(lineAmount) >= 0.001
        && (backendRender?.useBackendLineAmount || backendRender?.useAuthoritativeLineEntry)
    ) {
        return resolveSignedAdjustmentFromBackendLine(
            effectiveEntry,
            Math.abs(lineAmount),
            code,
            backendRender?.specialDtdPathCodes,
        );
    }

    // DTD line 级 adjustment 为用户带符号值；detail 行内为 flip 后存储，优先 line 级
    if (usesDtdAdjustmentPathScoped(code, backendRender?.specialDtdPathCodes) && Math.abs(lineAmount) >= 0.001) {
        return lineAmount;
    }
    if (parDetail && typeof parDetail === 'object') {
        if (isFlatEquityDetailForCode(code, parDetail)) {
            const flat = resolveFlatEquityAdjustment(parDetail);
            if (Math.abs(flat) >= 0.001) return flat;
        }
        // Loan field FS codes only：优先 detail.adjustment
        if (isLoanFieldFsCode(code)) {
            const top = Number(parDetail.adjustment) || 0;
            if (
                (Array.isArray(parDetail.bankLoans) || Array.isArray(parDetail.vehicleLoans)
                    || (code.trim().toUpperCase() === 'PL-FC-LI' && parDetail.interestSummary != null))
                && Math.abs(top) >= 0.001
            ) {
                return top;
            }
        }
        // PPE field WP→WP only：优先 detail.adjustment（nested field lists already consolidated）
        if (isPpeFieldSyntheticDetail(parDetail, code)) {
            const top = Number(parDetail.adjustment) || 0;
            if (Math.abs(top) >= 0.001) return top;
        }
        // IA field WP→WP only：优先 detail.adjustment（slim xxxAdj already consolidated）
        if (isIaFieldSyntheticDetail(parDetail, code)) {
            const top = Number(parDetail.adjustment) || 0;
            if (Math.abs(top) >= 0.001) return top;
        }
        // PL-AE-DEP Taxi nest：优先 detail.adjustment（由 amorChgAdjs 汇总，保留 intangibleRows）
        if (
            String(code || '').trim().toUpperCase() === 'PL-AE-DEP'
            && Array.isArray((parDetail as any).intangibleRows)
            && Math.abs(Number((parDetail as any).adjustment) || 0) >= 0.001
        ) {
            return Number((parDetail as any).adjustment) || 0;
        }
        // BS-EQ-IC + WP bag：优先 top-level adjustment，勿扫 shareholders/directors；无 bag 不改原路径
        if (String(code || '').trim().toUpperCase() === 'BS-EQ-IC') {
            const hasIcBag = Array.isArray(parDetail.shareholders)
                || Array.isArray(parDetail.directors)
                || (parDetail.registeredInfo != null && typeof parDetail.registeredInfo === 'object')
                || (parDetail.holdingCompany != null && typeof parDetail.holdingCompany === 'object');
            if (hasIcBag) {
                const top = Number(parDetail.adjustment) || 0;
                return Math.abs(top) >= 0.001 ? top : lineAmount;
            }
        }
        const sum = scanAllRows(parDetail).reduce((total, row) => total + row.adjustment, 0);
        if (Math.abs(sum) >= 0.001) return sum;
    }
    return lineAmount;
}

/** AdjustDrCrView 统一渲染上下文（覆盖 DTD / 非 DTD × from / to × 初始化 / Append） */
export interface DrCrClassifyRenderCtx {
    fromCode: string;
    fromIsDtd: boolean;
    /** from 走 DTD/scoped DTD 路径（含 TaxWp BS-CA-TAXR） */
    fromUsesDtdPath: boolean;
    fromAdjustment?: number;
    fromDtdUserEdited: boolean;
    /** fromPaper 用户已在 Dr/Cr 视图编辑过 */
    fromPaperUserEdited: boolean;
    fromDtdEffectiveEntry: 'Dr' | 'Cr' | null;
    toUserEditedCodes: Set<string>;
    /** Append Another Record 尚未组装完成时的 code */
    pendingAppendCodes: Set<string>;
    /** Append 写入 store 的带符号 line 级 adjustment（优先于 detail 汇总） */
    pendingAppendAdjustments: Record<string, number>;
    /** Append 组装后 pending 已消费时的 draft line 级 adjustment */
    draftAppendAdjustments?: Record<string, number | undefined>;
    staticPositionCodes?: Set<string>;
    /** TaxWp 等 scoped DTD path（如 BS-CA-TAXR refundable b/f） */
    specialDtdPathCodes?: Set<string>;
}

/** 非 DTD 初始化态：以 line.entry + adjustmentAmount 为准；用户编辑后改走动态分列 */
function shouldUseAuthoritativeNonDtdLineEntry(
    source: 'from' | 'to',
    codeUpper: string,
    effectiveEntry: 'Dr' | 'Cr' | null,
    ctx: Pick<DrCrClassifyRenderCtx, 'fromPaperUserEdited' | 'toUserEditedCodes'>,
): boolean {
    if (!effectiveEntry) return false;
    if (source === 'from' && ctx.fromPaperUserEdited) return false;
    if (source === 'to' && ctx.toUserEditedCodes.has(codeUpper)) return false;
    return true;
}

function resolveNonDtdDynamicRender(
    originalEntry: string,
    rowAdjustment: number,
    lineAdjustment: number,
): { position: 'Dr' | 'Cr'; displayAdjustment: number } {
    const adjForRender = Math.abs(rowAdjustment) >= 0.001 ? rowAdjustment : lineAdjustment;
    return {
        position: determineRenderPosition(originalEntry, adjForRender),
        displayAdjustment: adjForRender,
    };
}

/**
 * Append 非 DTD to：store entry 与 line 级符号分列冲突时的兼容规则。
 * - 非 DTD from：首配 pairing entry 可能与 append 相同 → 信 line 级 dynamic
 * - DTD from：detail 组装后符号可能 flip → 信 store entry
 */
function resolveAppendNonDtdRenderPosition(
    originalEntry: string,
    effectiveEntry: string | null,
    signedLineAdj: number,
    fromIsDtd: boolean,
): 'Dr' | 'Cr' {
    const dynamicPos = determineRenderPosition(originalEntry, signedLineAdj);
    const entryPos = effectiveEntry ? entryToRenderPosition(effectiveEntry) : null;
    if (entryPos && entryPos !== dynamicPos) {
        return fromIsDtd ? entryPos : dynamicPos;
    }
    return entryPos ?? dynamicPos;
}

export interface DrCrItemRenderInput {
    code: string;
    source: 'from' | 'to';
    originalEntry: string;
    rowAdjustment: number;
    lineAdjustment: number;
    effectiveEntry?: string | null;
    /** toPaper 在 lines 中的序号（0 = 首个 to，>0 = Append Another Record） */
    toIndex?: number;
    row?: any;
    perClient?: number;
}

export function buildDrCrClassifyRenderCtx(
    lineContexts: LineDrCrContext[],
    options?: ClassifyLinesForDrCrViewOptions,
): DrCrClassifyRenderCtx {
    const dtd = options?.dtdCtx;
    const fromCode = String(dtd?.fromCode || lineContexts[0]?.code || '').trim().toUpperCase();
    const toEdited = options?.toUserEditedCodes ?? dtd?.dtdClToUserEdited ?? new Set<string>();
    const pendingAppend = options?.pendingAppendCodes ?? new Set<string>();
    const pendingAppendAdj = options?.pendingAppendAdjustments ?? {};
    const draftAppendAdj = options?.draftAppendAdjustments ?? {};
    const specialDtd = options?.specialPathCodes;
    const fromUsesDtdPath = usesDtdAdjustmentPathScoped(fromCode, specialDtd);
    let fromDtdEffectiveEntry = dtd?.fromDtdEffectiveEntry ?? null;
    if (!fromDtdEffectiveEntry && fromUsesDtdPath) {
        const fromCtx = lineContexts[0];
        fromDtdEffectiveEntry = computeDtdPathFromEffectiveEntry(
            fromCode,
            fromCtx?.parDetail,
            dtd?.fromAdjustment ?? fromCtx?.lineAdjustment,
            options?.fromPaperUserEdited,
        ) ?? (
            fromCtx?.effectiveEntry === 'Dr' || fromCtx?.effectiveEntry === 'Cr'
                ? fromCtx.effectiveEntry
                : null
        );
    }
    return {
        fromCode,
        fromIsDtd: isDtdClCode(fromCode),
        fromUsesDtdPath,
        fromAdjustment: dtd?.fromAdjustment ?? lineContexts[0]?.lineAdjustment,
        fromDtdUserEdited: dtd?.fromDtdClUserEdited ?? false,
        fromPaperUserEdited: options?.fromPaperUserEdited ?? false,
        fromDtdEffectiveEntry,
        toUserEditedCodes: toEdited,
        pendingAppendCodes: pendingAppend,
        pendingAppendAdjustments: pendingAppendAdj,
        draftAppendAdjustments: draftAppendAdj,
        staticPositionCodes: options?.staticPositionCodes,
        specialDtdPathCodes: options?.specialPathCodes,
    };
}

/**
 * 统一解析 Dr/Cr 渲染列与展示用带符号 adjustment。
 *
 * | from | to | 时机 | 规则 |
 * | 非DTD from | 非DTD to | Append | line 级 dynamic；与 store entry 冲突时信 dynamic |
 * | 非DTD from | DTD to | Append | DTD line 级动态分列 |
 * | DTD from | 非DTD to | Append | store entry；与 dynamic 冲突时信 entry |
 * | DTD from | DTD to | Append | DTD line 级动态分列 |
 * | DTD | * | from 行 | DTD 专用 / 用户编辑 FsCategory |
 */
export function resolveDrCrItemRender(
    input: DrCrItemRenderInput,
    ctx: DrCrClassifyRenderCtx,
): { position: 'Dr' | 'Cr'; displayAdjustment: number } {
    const codeUpper = String(input.code || '').trim().toUpperCase();
    const isDtdCl = isDtdClCode(codeUpper);
    const usesDtdPath = usesDtdAdjustmentPathScoped(codeUpper, ctx.specialDtdPathCodes);
    const dtdOrig = usesDtdPath
        ? getOriginalEntryFromParent(codeUpper)
        : resolveDtdOriginalEntry(input.originalEntry);
    const lineAdj = Math.abs(input.lineAdjustment) >= 0.001
        ? input.lineAdjustment
        : input.rowAdjustment;
    const effectiveEntry = input.effectiveEntry === 'Dr' || input.effectiveEntry === 'Cr'
        ? input.effectiveEntry
        : null;

    const toUserEdited = ctx.toUserEditedCodes.has(codeUpper);
    const isPendingAppend = ctx.pendingAppendCodes.has(codeUpper);
    const isAppendOrUserEdited = toUserEdited || isPendingAppend;
    const toIndex = input.toIndex ?? 0;
    const fromUsesDtdPath = ctx.fromUsesDtdPath
        || usesDtdAdjustmentPathScoped(ctx.fromCode, ctx.specialDtdPathCodes);

    // DTD / scoped-DTD from → 首配 to（新建配对；覆盖错误的非 DTD 配对 entry）
    // BS-EQ-IC 始终走非 DTD `_synthetic`（line.entry / amount / details.adjustment），勿被 DTD 配对覆盖
    if (input.source === 'to' && fromUsesDtdPath && !ctx.fromPaperUserEdited && !isAppendOrUserEdited
        && !toUserEdited && toIndex === 0 && ctx.fromDtdEffectiveEntry != null
        && codeUpper !== 'BS-EQ-IC') {
        const isBackendStaticTo = usesStaticRenderPosition(codeUpper, ctx.staticPositionCodes) && !!effectiveEntry;
        if (!isBackendStaticTo) {
            const paired = resolveToPaperRenderFromDtdPairing(
                ctx.fromCode,
                ctx.fromAdjustment ?? lineAdj,
                codeUpper,
                ctx.specialDtdPathCodes,
            );
            return {position: paired.position, displayAdjustment: paired.storedAdj};
        }
    }

    // 非 DTD 初始化 / 后端回显：line.entry + adjustmentAmount 优先于 DTD from 配对重算
    if (!usesDtdPath && shouldUseAuthoritativeNonDtdLineEntry(input.source, codeUpper, effectiveEntry, ctx)) {
        const absAmount = Math.abs(Number(input.lineAdjustment)) >= 0.001
            ? Math.abs(Number(input.lineAdjustment))
            : Math.abs(Number(input.rowAdjustment));
        // BS-EQ-IC（权益 Cr）：同侧为正，与 determineRenderPosition / updateLineAdjustment 一致
        if (codeUpper === 'BS-EQ-IC') {
            const sameSide = getAdjustmentSignMultiplier(input.originalEntry, effectiveEntry!) > 0;
            return {
                position: entryToRenderPosition(effectiveEntry!),
                displayAdjustment: sameSide ? absAmount : -absAmount,
            };
        }
        return {
            position: entryToRenderPosition(effectiveEntry!),
            displayAdjustment: effectiveEntry === 'Cr' ? -absAmount : absAmount,
        };
    }

    // DTD 后端回显 / 已落库 entry（special flow）：from/to 均信任 line.entry + adjustmentAmount
    if (shouldUseDtdAuthoritativeLineEntryForRender(
        input.source,
        usesDtdPath,
        effectiveEntry,
        ctx.fromPaperUserEdited,
        toUserEdited,
        isAppendOrUserEdited,
    )) {
        const absAmount = Math.abs(Number(input.lineAdjustment)) >= 0.001
            ? Math.abs(Number(input.lineAdjustment))
            : Math.abs(Number(input.rowAdjustment));
        if (absAmount >= 0.001) {
            return resolveDtdAuthoritativeLineEntryRender(
                effectiveEntry!,
                input.lineAdjustment,
                input.rowAdjustment,
                input.code,
            );
        }
    }

    // DTD from → 首配 to（BS-CL-DTD 专用，尚无 line.entry；scoped 已在上方处理）
    // BS-EQ-IC 除外：非 DTD schedule-level adj
    if (input.source === 'to' && ctx.fromIsDtd && !ctx.fromDtdUserEdited && !isAppendOrUserEdited
        && toIndex === 0 && ctx.fromDtdEffectiveEntry != null && !effectiveEntry
        && codeUpper !== 'BS-EQ-IC') {
        const paired = resolveToPaperRenderFromDtdPairing(
            ctx.fromCode,
            ctx.fromAdjustment ?? lineAdj,
            codeUpper,
            ctx.specialDtdPathCodes,
        );
        return {position: paired.position, displayAdjustment: paired.storedAdj};
    }

    // 非 DTD 静态后端行（兜底，与 authoritative 一致）
    if (usesStaticRenderPosition(codeUpper, ctx.staticPositionCodes) && !usesDtdPath) {
        const renderEntry = effectiveEntry ?? input.originalEntry;
        const absAmount = Math.abs(Number(input.lineAdjustment)) >= 0.001
            ? Math.abs(Number(input.lineAdjustment))
            : Math.abs(Number(input.rowAdjustment));
        const displayAdjustment = renderEntry === 'Cr' ? -absAmount : absAmount;
        return {
            position: entryToRenderPosition(renderEntry),
            displayAdjustment,
        };
    }

    if (input.source === 'from') {
        if (usesDtdPath && !isDtdCl) {
            let adjForPosition: number;
            if (ctx.fromPaperUserEdited) {
                adjForPosition = ctx.fromAdjustment !== undefined && Math.abs(ctx.fromAdjustment) >= 0.001
                    ? Number(ctx.fromAdjustment)
                    : (Math.abs(input.lineAdjustment) >= 0.001
                        ? input.lineAdjustment
                        : -Number(input.rowAdjustment) || 0);
            } else {
                adjForPosition = resolveDtdUserSignedRowAdjustment(
                    input.rowAdjustment,
                    Math.abs(input.lineAdjustment) >= 0.001
                        ? input.lineAdjustment
                        : ctx.fromAdjustment,
                    false,
                );
            }
            return {
                position: determineDtdClRenderPosition(adjForPosition, dtdOrig),
                displayAdjustment: adjForPosition,
            };
        }
        if (isDtdCl && ctx.fromIsDtd && codeUpper === ctx.fromCode) {
            const rowForSide = {
                perClient: Number(input.perClient ?? input.row?.perClient) || 0,
                code: input.row?.code ?? codeUpper,
                adjustment: Number(input.rowAdjustment) || 0,
            };
            if (!rowBelongsToDueFromToDirFromPaper(ctx.fromCode, rowForSide)) {
                // DTD 后端回显：detail row.code 可能仍为 BS-CA-DIR，仅初始化时走 line 级 DTD 分列
                if (!ctx.fromDtdUserEdited) {
                    const signedLine = Math.abs(input.lineAdjustment) >= 0.001
                        ? input.lineAdjustment
                        : (ctx.fromAdjustment ?? input.rowAdjustment);
                    const adjForPosition = resolveDtdUserSignedRowAdjustment(
                        input.rowAdjustment,
                        signedLine,
                        false,
                    );
                    return {
                        position: determineDtdClRenderPosition(adjForPosition, dtdOrig),
                        displayAdjustment: adjForPosition,
                    };
                }
                return {
                    position: determineRenderPosition(input.originalEntry, input.rowAdjustment),
                    displayAdjustment: input.rowAdjustment,
                };
            }
            if (ctx.fromDtdUserEdited) {
                const signed = ctx.fromAdjustment !== undefined && Math.abs(ctx.fromAdjustment) >= 0.001
                    ? Number(ctx.fromAdjustment)
                    : -Number(input.rowAdjustment) || 0;
                return {
                    position: determineDtdClRenderPosition(signed, dtdOrig),
                    displayAdjustment: signed,
                };
            }
            const adjForPosition = resolveDtdUserSignedRowAdjustment(
                input.rowAdjustment,
                ctx.fromAdjustment,
                false,
            );
            return {
                position: determineDtdClRenderPosition(adjForPosition, dtdOrig),
                displayAdjustment: adjForPosition,
            };
        }
        return resolveNonDtdDynamicRender(input.originalEntry, input.rowAdjustment, lineAdj);
    }

    // 附加 toPaper（Append Another Record, toIndex > 0）
    if (toIndex > 0) {
        const pendingAdj = Number(ctx.pendingAppendAdjustments[codeUpper]) || 0;
        const draftAppendAdj = Number(ctx.draftAppendAdjustments?.[codeUpper]) || 0;
        const storeAppendAdj = Math.abs(pendingAdj) >= 0.001
            ? pendingAdj
            : (Math.abs(draftAppendAdj) >= 0.001 ? draftAppendAdj : 0);
        // Append 分列仅用 line 级 adjustment，不用 detail 行内符号（DTD from 组装后 detail 可能反号）
        const signedLineAdj = Math.abs(storeAppendAdj) >= 0.001
            ? storeAppendAdj
            : (Math.abs(input.lineAdjustment) >= 0.001 ? input.lineAdjustment : 0);
        // Append BS-CL-DTD：初始化走 DTD 专用分列；用户编辑后负号切列
        if (usesDtdPath) {
            if (toUserEdited) {
                return resolveDtdToPaperUserEditedRender(
                    signedLineAdj,
                    input.originalEntry,
                    effectiveEntry,
                    codeUpper,
                );
            }
            return {
                position: determineDtdClRenderPosition(signedLineAdj, dtdOrig),
                displayAdjustment: signedLineAdj,
            };
        }
        // Append 非 DTD：用户已编辑 detail 行时，与首配 to 一致按行级 adjustment 动态分列
        if (toUserEdited && Math.abs(input.rowAdjustment) >= 0.001) {
            const signedLineForRow = Math.abs(input.lineAdjustment) >= 0.001
                ? input.lineAdjustment
                : signedLineAdj;
            return resolveNonDtdDynamicRender(
                input.originalEntry,
                input.rowAdjustment,
                signedLineForRow,
            );
        }
        // Append 非 DTD 初始化（pending / 尚无 detail 行）：line 级分列
        if (isAppendOrUserEdited && Math.abs(signedLineAdj) >= 0.001) {
            return {
                position: resolveAppendNonDtdRenderPosition(
                    input.originalEntry,
                    effectiveEntry,
                    signedLineAdj,
                    ctx.fromIsDtd,
                ),
                displayAdjustment: signedLineAdj,
            };
        }
        if (effectiveEntry) {
            return {
                position: entryToRenderPosition(effectiveEntry),
                displayAdjustment: signedLineAdj,
            };
        }
        return {
            position: determineRenderPosition(input.originalEntry, signedLineAdj),
            displayAdjustment: signedLineAdj,
        };
    }

    // 首配 to（toIndex === 0）用户编辑：动态分列
    if (isAppendOrUserEdited) {
        const signedLineAdj = Math.abs(input.lineAdjustment) >= 0.001
            ? input.lineAdjustment
            : lineAdj;
        if (usesDtdPath && toUserEdited) {
            return resolveDtdToPaperUserEditedRender(
                signedLineAdj,
                input.originalEntry,
                effectiveEntry,
                codeUpper,
            );
        }
        if (usesDtdPath) {
            return {
                position: determineDtdClRenderPosition(signedLineAdj, dtdOrig),
                displayAdjustment: signedLineAdj,
            };
        }
        return resolveNonDtdDynamicRender(input.originalEntry, input.rowAdjustment, signedLineAdj);
    }

    // 首配 to（初始化配对）：使用 store effectiveEntry 固定列
    if (effectiveEntry) {
        const displayAdj = usesDtdPath && Math.abs(input.lineAdjustment) >= 0.001
            ? input.lineAdjustment
            : lineAdj;
        return {
            position: entryToRenderPosition(effectiveEntry),
            displayAdjustment: displayAdj,
        };
    }

    // 场景 3：非 DTD from → 首配 DTD to（尚无 entries 落库时的回退）
    if (usesDtdPath) {
        return {
            position: determineDtdClRenderPosition(lineAdj, dtdOrig),
            displayAdjustment: lineAdj,
        };
    }

    return {
        position: determineRenderPosition(input.originalEntry, lineAdj),
        displayAdjustment: lineAdj,
    };
}

function scanDetailRowsForRender(
    parDetail: any,
    code: string,
    extraRowKeys?: Set<string>,
): AdjustedRowInfo[] {
    if (!parDetail || typeof parDetail !== 'object') return [];

    // BS-EQ-DIV / BS-EQ-AL flat：仅 flat detail 走 synthetic；BS-EQ-AL 发起带 commonRow 时走常规行扫描
    if (isFlatEquityDetailForCode(code, parDetail)) {
        const adj = resolveFlatEquityAdjustment(parDetail);
        if (Math.abs(adj) >= 0.001 || extraRowKeys?.has(`${code}:_synthetic:0`)) {
            return scanFlatEquityAdjustmentRows(parDetail, true);
        }
        return [];
    }

    // Loan field FS codes only: fall through to line-level `_synthetic`
    if (isLoanFieldFsCode(code)) {
        const isLoanDetail = Array.isArray(parDetail.bankLoans) || Array.isArray(parDetail.vehicleLoans);
        const isInterestSummary = String(code).trim().toUpperCase() === 'PL-FC-LI'
            && parDetail.interestSummary != null
            && typeof parDetail.interestSummary === 'object'
            && !Array.isArray(parDetail.interestSummary);
        if (isLoanDetail || isInterestSummary) {
            return [];
        }
    }

    // PL-AE-REN field WP→WP: fall through to line-level `_synthetic` (top-level adjustment)
    if (String(code).trim().toUpperCase() === 'PL-AE-REN' && isRentalFieldWpDetail(parDetail)) {
        return [];
    }

    // PPE field WP→WP only: fall through to line-level `_synthetic` (top-level adjustment)
    if (isPpeFieldSyntheticDetail(parDetail, code)) {
        return [];
    }

    // IA field WP→WP: fall through to line-level `_synthetic` (slim xxxAdj + adjustment)
    if (isIaFieldSyntheticDetail(parDetail, code)) {
        return [];
    }

    // BS-EQ-IC：schedule-level adj — WP bag (shareholders/directors/…) 不参与行扫描；
    // AdjustTabsDialog 一律用 line `_synthetic`（非 DTD）编辑 entry / amount / details.adjustment
    if (String(code).trim().toUpperCase() === 'BS-EQ-IC') {
        return [];
    }

    const shouldInclude = (arrayName: string, rowIndex: number, adj: number): boolean => {
        if (Math.abs(adj) >= 0.001) return true;
        if (extraRowKeys?.has(`${code}:${arrayName}:${rowIndex}`)) return true;
        return false;
    };

    const result: AdjustedRowInfo[] = [];
    for (const [key, value] of Object.entries(parDetail)) {
        if (!Array.isArray(value)) continue;
        if (key === 'bankLoans' || key === 'vehicleLoans') continue;
        (value as any[]).forEach((row: any, idx: number) => {
            if (!row || typeof row !== 'object') return;
            if (
                !('adjustment' in row)
                && !('perClient' in row)
                && !('item' in row)
                && !('salary' in row)
                && !('name' in row)
                && !('directorName' in row)
            ) return;

            const adj = Number(row.adjustment) || 0;
            if (shouldInclude(key, idx, adj)) {
                result.push(buildRowInfo(key, idx, row, adj));
            }

            if (row.mpf && typeof row.mpf === 'object' && 'adjustment' in row.mpf) {
                const mpfAdj = Number(row.mpf.adjustment) || 0;
                const mpfKey = `${code}:${key}:${idx}:mpf`;
                if (Math.abs(mpfAdj) >= 0.001 || extraRowKeys?.has(mpfKey)) {
                    result.push(buildRowInfo(key, idx, row, mpfAdj, 'mpf'));
                }
            }
        });
    }

    if (shouldInclude('_synthetic', 0, Number(parDetail.adjustment) || 0)) {
        result.push(...scanFlatEquityAdjustmentRows(parDetail, true));
    }

    return result;
}

/**
 * 将 lines 中所有节点分类到 Dr/Cr 渲染列（统一 resolveDrCrItemRender）。
 */
export interface ClassifyLinesForDrCrViewOptions {
    dtdCtx?: DtdClBsClPairingCtx | null;
    /** fromPaper 用户已在 Dr/Cr 视图编辑 */
    fromPaperUserEdited?: boolean;
    /** toPaper 已 Append / 用户编辑的 code（含 Append 后 DTD / 非 DTD） */
    toUserEditedCodes?: Set<string>;
    /** Append Another Record 流程中、尚未完成组装的 code */
    pendingAppendCodes?: Set<string>;
    /** Append 带符号 line 级 adjustment（code → amount） */
    pendingAppendAdjustments?: Record<string, number>;
    /** Append 组装后 pending 已消费时的 draft line 级 adjustment */
    draftAppendAdjustments?: Record<string, number | undefined>;
    origEntries?: Record<string, string>;
    staticPositionCodes?: Set<string>;
    specialPathCodes?: Set<string>;
}

export function classifyLinesForDrCrView(
    lineContexts: LineDrCrContext[],
    extraRowKeys?: Set<string>,
    options?: ClassifyLinesForDrCrViewOptions,
): { drItems: DrCrRenderItem[]; crItems: DrCrRenderItem[] } {
    const drItems: DrCrRenderItem[] = [];
    const crItems: DrCrRenderItem[] = [];
    const renderCtx = buildDrCrClassifyRenderCtx(lineContexts, options);

    const pushItem = (item: DrCrRenderItem) => {
        (item.position === 'Dr' ? drItems : crItems).push(item);
    };

    for (const ctx of lineContexts) {
        const detailRows = scanDetailRowsForRender(ctx.parDetail, ctx.code, extraRowKeys);

        if (detailRows.length > 0) {
            for (const row of detailRows) {
                const {position, displayAdjustment} = resolveDrCrItemRender({
                    code: ctx.code,
                    source: ctx.source,
                    originalEntry: ctx.originalEntry,
                    rowAdjustment: row.adjustment,
                    lineAdjustment: ctx.lineAdjustment,
                    effectiveEntry: ctx.effectiveEntry,
                    toIndex: ctx.toIndex,
                    row: row.row,
                    perClient: row.perClient,
                }, renderCtx);
                pushItem({
                    ...row,
                    adjustment: displayAdjustment,
                    code: ctx.code,
                    codeName: ctx.codeName,
                    originalEntry: ctx.originalEntry,
                    entry: position,
                    source: ctx.source,
                    toIndex: ctx.toIndex,
                    position,
                });
            }
            continue;
        }

        if (Math.abs(ctx.lineAdjustment) < 0.001) continue;

        const {position, displayAdjustment} = resolveDrCrItemRender({
            code: ctx.code,
            source: ctx.source,
            originalEntry: ctx.originalEntry,
            rowAdjustment: ctx.lineAdjustment,
            lineAdjustment: ctx.lineAdjustment,
            effectiveEntry: ctx.effectiveEntry,
            toIndex: ctx.toIndex,
        }, renderCtx);
        pushItem({
            arrayName: '_synthetic',
            rowIndex: 0,
            row: {
                item: ctx.codeName,
                perClient: isDtdClCode(ctx.code) ? -1 : 0,
                adjustment: displayAdjustment,
            },
            adjustment: displayAdjustment,
            rowKey: ctx.codeName,
            perClient: isDtdClCode(ctx.code) ? -1 : 0,
            code: ctx.code,
            codeName: ctx.codeName,
            originalEntry: ctx.originalEntry,
            entry: position,
            source: ctx.source,
            toIndex: ctx.toIndex,
            position,
            isSynthetic: true,
        });
    }

    return partitionRenderItemsByPosition(drItems, crItems);
}
